import validateAsync from "@/utils/ZodHelpers/validateAsync";
import { z } from "zod";
import { useForm } from "@mantine/form";
import { Button, Grid, TextInput } from "@monolith-forensics/monolith-ui";
import { forwardRef, useImperativeHandle, useEffect } from "react";
import ButtonMenu from "@/components/Forms/components/ButtonMenu";
import UserSelector from "@/components/Forms/components/Selectors/UserSelector";
import diffFormData from "@/components/Forms/utils/diffFormData";
import { Office } from "@/Features/Offices";

export interface OfficeFormData
  extends Omit<Office, "office_id" | "total_users" | "manager">,
    Partial<Pick<Office, "office_id" | "total_users" | "manager">> {}

export interface EditOfficeFormData extends Partial<OfficeFormData> {}

interface DefaultOfficeFormProps {
  defaultFormData?: OfficeFormData;
  buttonProps?: { cancelText?: string; submitText?: string };
  onSubmit: (data: OfficeFormData, diffData?: EditOfficeFormData) => void;
  onCancel: () => void;
  showButtons?: boolean;
  controlled?: boolean;
  onTouchedChange?: (touched: boolean) => void;
}

export interface DefaultOfficeFormRef {
  isTouched: () => boolean;
  resetTouched: () => void;
  handleSubmit: () => Promise<void>;
}

const officeValidationSchema = z.object({
  office_name: z
    .string({
      required_error: "Office name is required",
    })
    .min(1, { message: "Office name is required" }),
  office_address: z.string().nullable().optional(),
  office_city: z.string().nullable().optional(),
  office_region: z.string().nullable().optional(),
  office_postal_code: z.string().nullable().optional(),
  office_phone: z.string().nullable().optional(),
  manager_id: z.coerce.number().nullable().optional(),
});

export const DefaultOfficeForm = forwardRef<
  DefaultOfficeFormRef,
  DefaultOfficeFormProps
>(
  (
    {
      defaultFormData,
      buttonProps,
      onSubmit,
      onCancel,
      showButtons = true,
      controlled = false,
      onTouchedChange,
    },
    ref
  ) => {
    const form = useForm<OfficeFormData>({
      mode: controlled ? "controlled" : "uncontrolled",
      initialValues: defaultFormData,
    });

    const handleSubmit = async () => {
      const formData = form.getValues();

      const validationResult = await validateAsync(officeValidationSchema)(
        formData
      );
      if (validationResult?.hasErrors) {
        console.error("Validation Errors", validationResult?.errors);
        form.setErrors(validationResult?.errors);
        return;
      }

      const diffData = diffFormData(defaultFormData, formData);
      onSubmit?.(formData, diffData);
    };

    const handleCancel = () => {
      form.reset();
      onCancel?.();
    };

    useImperativeHandle(ref, () => ({
      isTouched: () => form.isTouched(),
      resetTouched: () => form.resetTouched(),
      handleSubmit,
    }));

    // Notify parent when touched state changes
    useEffect(() => {
      if (controlled && onTouchedChange) {
        onTouchedChange(form.isTouched());
      }
    }, [form.values, controlled, onTouchedChange]);

    return (
      <>
        <Grid col={2}>
          <TextInput
            size="sm"
            variant="outlined"
            label="Office Name"
            required={true}
            placeholder="Enter office name"
            description="The name of the office"
            key={form.key("office_name")}
            {...form.getInputProps("office_name")}
          />
          <UserSelector
            label="Manager"
            defaultValue={defaultFormData?.manager_id?.toString()}
            query={{
              includeObservers: true,
              includeInactive: false,
            }}
            key={form.key("manager_id")}
            {...form.getInputProps("manager_id")}
          />
          <TextInput
            size="sm"
            variant="outlined"
            label="Address"
            placeholder="Enter office address"
            description="The street address of the office"
            key={form.key("office_address")}
            {...form.getInputProps("office_address")}
          />
          <TextInput
            size="sm"
            variant="outlined"
            label="City"
            placeholder="Enter office city"
            description="The city where the office is located"
            key={form.key("office_city")}
            {...form.getInputProps("office_city")}
          />
          <TextInput
            size="sm"
            variant="outlined"
            label="State/Province"
            placeholder="Enter state or province"
            description="The state or province where the office is located"
            key={form.key("office_region")}
            {...form.getInputProps("office_region")}
          />
          <TextInput
            size="sm"
            variant="outlined"
            label="Postal Code"
            placeholder="Enter postal code"
            description="The postal/zip code of the office"
            key={form.key("office_postal_code")}
            {...form.getInputProps("office_postal_code")}
          />
          <TextInput
            size="sm"
            variant="outlined"
            label="Phone"
            placeholder="Enter office phone number"
            description="The main phone number for the office"
            key={form.key("office_phone")}
            {...form.getInputProps("office_phone")}
          />
        </Grid>
        {showButtons && (
          <ButtonMenu>
            <Button size="xs" variant="subtle" onClick={handleCancel}>
              {buttonProps?.cancelText || "Cancel"}
            </Button>
            <Button
              size="xs"
              color="primary"
              variant="contained"
              onClick={handleSubmit}
            >
              {buttonProps?.submitText || "Submit"}
            </Button>
          </ButtonMenu>
        )}
      </>
    );
  }
);
